import { getIn, useFormikContext } from 'formik'
import { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { ReactNode, useRef, useState } from 'react'

import { PopoverInput } from '@ui/components/Input'

import { NameDto } from '@model/NameDto'

import { DeepNonNullable } from '../types/DeepNonNullable'
import { DeepRequired } from '../types/DeepRequired'
import { TypedFieldProps } from '../types/TypedFieldProps'
import { ValidateField } from '../types/ValidateField'
import { getLabel, getPlaceholder } from './helpers'

type FieldConfig = Omit<FormikFieldConfig, 'name'>

interface FormFieldProps extends FieldConfig {
  namePrefix?: string
  helpText?: string
  highlighted?: boolean
  icon?: React.ReactNode
  leftIcon?: React.ReactNode
  hint?: string
  tooltip?: string
  onChange?(value: React.ChangeEvent<HTMLInputElement>): void
  onClick?(): void
  renderTooltip?(): ReactNode
  options?: NameDto<string | number>[]
  hasCheckMark?: boolean
  searchable?: boolean
  dropdownListVariant?: 'compact' | 'default'
  wrapText?: boolean
}

export function RawSelectFormField<FormValues>(
  props: FormFieldProps & TypedFieldProps<FormValues>
) {
  const {
    namePrefix = '',
    name = '',
    hint,
    options = [],
    hasCheckMark,
    searchable,
    dropdownListVariant,
    wrapText,
    ...inputProps
  } = props

  const fieldRef = useRef<HTMLDivElement>(null)
  const context = useFormikContext<FormValues>()
  const [search, setSearch] = useState('')

  const fieldName = namePrefix + name
  const value = getValue(context.values, props.value, name)

  const label = getLabel(props)
  const placeholder = getPlaceholder(props)

  const labelProps = label ? { label, placeholder } : { label: undefined, placeholder: undefined }

  const dropdownOptions = options

  const handleDropdownChange = (option: NameDto<string | number>) => {
    context.setFieldValue(fieldName, option.id)
  }

  return (
    <div ref={fieldRef}>
      <PopoverInput<NameDto<string | number>>
        value={value}
        searchInput={
          searchable
            ? {
                value: search,
                handleChange: (e) => setSearch(e.target.value),
                handleClear: () => setSearch(''),
              }
            : undefined
        }
        onChange={handleDropdownChange}
        options={dropdownOptions.filter((option) =>
          option.name.toLowerCase().includes(search.toLowerCase())
        )}
        {...labelProps}
        hint={hint}
        name={fieldName}
        required={props.required}
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        hasCheckMark={hasCheckMark}
        dropdownListVariant={dropdownListVariant}
        showLabel={props.showLabel}
        wrapText={wrapText}
      />
    </div>
  )
}

const getValue = <FormValues,>(values: FormValues, value: string, name: string) => {
  const fieldValue = getIn(values, name)

  return value || fieldValue || ''
}

export function createFormSelectField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ) {
    return <RawSelectFormField {...props} />
  }
}
